import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { ROLES } from "../../../constants/roles";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { i18n } from "../../../translate/i18n";
import MasterPageBreadcrumb from "../../../layout/master/MasterPageBreadcrumb";
import { getMasterRouteMeta } from "../../../layout/master/masterRouteMeta";
import MasterSpinner from "../../../components/Master/MasterSpinner";
import MasterModal from "../../../components/Master/MasterModal";
import MasterConfirmationModal from "../../../components/Master/MasterConfirmationModal";
import AuthField from "../../../components/Auth/AuthField";
import "../Plans/plans-page.css";

const emptyForm = {
	name: "",
	discountType: "PERCENTAGE",
	discountValue: "",
	expiresAt: "",
	lifetime: true,
	emailRef: "",
	active: true,
};

const COUPON_COLORS = ["#6366f1", "#8b5cf6", "#06b6d4", "#f59e0b"];

function IconPlus() {
	return (
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<line x1="12" y1="5" x2="12" y2="19" />
			<line x1="5" y1="12" x2="19" y2="12" />
		</svg>
	);
}

function IconEdit() {
	return (
		<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
			<path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
		</svg>
	);
}

function IconTrash() {
	return (
		<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
		</svg>
	);
}

function formatDiscount(type, value) {
	const n = Number(value);
	if (Number.isNaN(n)) return "—";
	if (type === "PERCENTAGE") return `${n}%`;
	return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatExpiresAt(value) {
	if (!value) return i18n.t("master.coupons.noExpiry");
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toLocaleDateString("pt-BR");
}

function toDateInputValue(value) {
	if (!value) return "";
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return "";
	return d.toISOString().slice(0, 10);
}

function CouponCard({ coupon, color, deleting, onEdit, onDelete }) {
	const [hovered, setHovered] = useState(false);

	return (
		<article
			className="mp-card"
			style={{
				borderColor: hovered ? `${color}55` : undefined,
				boxShadow: hovered ? `0 8px 32px ${color}22` : undefined,
			}}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
		>
			<div
				className="mp-card__accent"
				style={{ background: `linear-gradient(90deg, ${color}, ${color}55)` }}
			/>

			<div className="mp-card__header">
				<div className="min-w-0">
					<div className="mp-card__name-row">
						<h3 className="mp-card__name">{coupon.name}</h3>
					</div>
					<div className="mp-card__meta">
						<span>#{coupon.id}</span>
						<span className="mp-card__meta-sep">·</span>
						<span>
							{coupon.discountType === "PERCENTAGE"
								? i18n.t("master.coupons.typePercentage")
								: i18n.t("master.coupons.typeFixed")}
						</span>
					</div>
				</div>
				<span
					className={`mp-card__badge ${
						coupon.active ? "mp-card__badge--active" : "mp-card__badge--inactive"
					}`}
				>
					{coupon.active
						? i18n.t("master.pages.statusActive")
						: i18n.t("master.pages.statusInactive")}
				</span>
			</div>

			<div
				className="mp-card__featured"
				style={{
					background: `linear-gradient(135deg, ${color}18 0%, ${color}08 100%)`,
					border: `1px solid ${color}25`,
				}}
			>
				<div className="mp-card__featured-label" style={{ color }}>
					{i18n.t("master.coupons.fieldDiscountValue")}
				</div>
				<div className="mp-card__featured-price">
					{formatDiscount(coupon.discountType, coupon.discountValue)}
				</div>
				<div className="mp-card__featured-hint">
					{coupon.discountType === "PERCENTAGE"
						? i18n.t("master.coupons.typePercentage")
						: i18n.t("master.coupons.typeFixed")}
				</div>
			</div>

			<div className="mp-card__prices">
				<div className="mp-card__price-row">
					<span className="mp-card__price-label">{i18n.t("master.coupons.fieldExpiresAt")}</span>
					<span className="mp-card__price-value">{formatExpiresAt(coupon.expiresAt)}</span>
				</div>
				<div className="mp-card__price-row">
					<span className="mp-card__price-label">{i18n.t("master.coupons.fieldEmailRef")}</span>
					<span className="mp-card__price-value">
						{coupon.emailRef || i18n.t("master.coupons.anyEmail")}
					</span>
				</div>
			</div>

			<div className="mp-card__actions">
				<button
					type="button"
					className="mp-card__btn-edit"
					onClick={() => onEdit(coupon)}
					onMouseEnter={(e) => {
						e.currentTarget.style.background = `${color}22`;
						e.currentTarget.style.borderColor = `${color}66`;
					}}
					onMouseLeave={(e) => {
						e.currentTarget.style.background = "";
						e.currentTarget.style.borderColor = "";
					}}
				>
					<IconEdit />
					{i18n.t("master.coupons.edit")}
				</button>
				<button
					type="button"
					className="mp-card__btn-delete"
					disabled={deleting}
					title={i18n.t("master.coupons.delete")}
					aria-label={i18n.t("master.coupons.delete")}
					onClick={() => onDelete(coupon)}
				>
					<IconTrash />
				</button>
			</div>
		</article>
	);
}

export default function MasterCoupons() {
	const history = useHistory();
	const { user, loading } = useContext(AuthContext);
	const routeMeta = getMasterRouteMeta("/admin-master/coupons");
	const [coupons, setCoupons] = useState([]);
	const [loadingCoupons, setLoadingCoupons] = useState(true);
	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form, setForm] = useState(emptyForm);
	const [saving, setSaving] = useState(false);
	const [deletingId, setDeletingId] = useState(null);
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
	const [couponToDelete, setCouponToDelete] = useState(null);

	const fetchCoupons = useCallback(() => {
		setLoadingCoupons(true);
		return api
			.get("/master/coupons")
			.then(({ data }) => setCoupons(data || []))
			.catch((err) => toastError(err))
			.finally(() => setLoadingCoupons(false));
	}, []);

	useEffect(() => {
		if (loading) return;
		if (!user || user.profile !== ROLES.master) {
			history.replace("/");
			return;
		}
		fetchCoupons();
	}, [user, loading, history, fetchCoupons]);

	const sortedCoupons = useMemo(
		() =>
			[...coupons].sort((a, b) =>
				String(a.name || "").localeCompare(String(b.name || ""))
			),
		[coupons]
	);

	const activeCount = useMemo(
		() => coupons.filter((c) => c.active).length,
		[coupons]
	);
	const inactiveCount = coupons.length - activeCount;

	const openCreate = () => {
		setEditing(null);
		setForm(emptyForm);
		setModalOpen(true);
	};

	const openEdit = (coupon) => {
		setEditing(coupon);
		setForm({
			name: coupon.name || "",
			discountType: coupon.discountType || "PERCENTAGE",
			discountValue: String(coupon.discountValue ?? ""),
			expiresAt: toDateInputValue(coupon.expiresAt),
			lifetime: !coupon.expiresAt,
			emailRef: coupon.emailRef || "",
			active: Boolean(coupon.active),
		});
		setModalOpen(true);
	};

	const closeModal = () => {
		if (saving) return;
		setModalOpen(false);
		setEditing(null);
		setForm(emptyForm);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const payload = {
			name: form.name.trim(),
			discountType: form.discountType,
			discountValue: Number(form.discountValue),
			expiresAt: form.lifetime ? null : form.expiresAt || null,
			emailRef: form.emailRef.trim() || null,
			active: Boolean(form.active),
		};
		setSaving(true);
		try {
			if (editing) {
				await api.put(`/master/coupons/${editing.id}`, payload);
				toast.success(i18n.t("master.coupons.toastUpdated"));
			} else {
				await api.post("/master/coupons", payload);
				toast.success(i18n.t("master.coupons.toastCreated"));
			}
			setModalOpen(false);
			setEditing(null);
			setForm(emptyForm);
			await fetchCoupons();
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const handleOpenDeleteConfirm = (coupon) => {
		setCouponToDelete(coupon);
		setConfirmDeleteOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (!couponToDelete) return;
		setDeletingId(couponToDelete.id);
		try {
			const { data } = await api.delete(`/master/coupons/${couponToDelete.id}`);
			if (data?.deleted) {
				toast.success(i18n.t("master.coupons.toastDeleted"));
			} else {
				toast.success(i18n.t("master.coupons.toastDisabled"));
			}
			setCouponToDelete(null);
			await fetchCoupons();
		} catch (err) {
			toastError(err);
		} finally {
			setDeletingId(null);
		}
	};

	if (loading || !user || user.profile !== ROLES.master) {
		return null;
	}

	const modalFooter = (
		<>
			<button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>
				{i18n.t("master.coupons.cancel")}
			</button>
			<button
				type="submit"
				form="master-coupon-form"
				className="btn-primary"
				disabled={saving}
			>
				{saving ? i18n.t("master.coupons.saving") : i18n.t("master.coupons.save")}
			</button>
		</>
	);

	const stats = [
		{ labelKey: "master.coupons.statTotal", value: coupons.length, color: "#6366f1" },
		{ labelKey: "master.coupons.statActive", value: activeCount, color: "#10b981" },
		{ labelKey: "master.coupons.statInactive", value: inactiveCount, color: "#6b7280" },
	];

	return (
		<div className="master-plans-page mx-auto w-full max-w-none box-border">
			<MasterPageBreadcrumb
				title={i18n.t(routeMeta.titleKey)}
				breadcrumbs={routeMeta.breadcrumbs}
			/>

			<div className="master-plans-page__top">
				<div>
					<h1 className="master-plans-page__title">{i18n.t("master.coupons.pageTitle")}</h1>
					<p className="master-plans-page__subtitle">
						{i18n.t("master.coupons.pageSubtitle")}
					</p>
				</div>
				<button type="button" className="master-plans-page__create" onClick={openCreate}>
					<IconPlus />
					{i18n.t("master.coupons.create")}
				</button>
			</div>

			<div className="master-plans-page__stats">
				{stats.map((stat) => (
					<div key={stat.labelKey} className="master-plans-page__stat">
						<span
							className="master-plans-page__stat-dot"
							style={{
								background: stat.color,
								boxShadow: `0 0 6px ${stat.color}`,
							}}
						/>
						<span className="master-plans-page__stat-label">{i18n.t(stat.labelKey)}</span>
						<span className="master-plans-page__stat-value">{stat.value}</span>
					</div>
				))}
			</div>

			<div className="master-plans-page__section">
				<span className="master-plans-page__section-title">
					{i18n.t("master.coupons.listTitle")}
				</span>
				<span className="master-plans-page__section-count">
					{coupons.length} {i18n.t("master.coupons.countLabel")}
				</span>
			</div>

			{loadingCoupons ? (
				<div className="master-plans-page__loading">
					<MasterSpinner />
					<span>{i18n.t("master.coupons.loading")}</span>
				</div>
			) : sortedCoupons.length === 0 ? (
				<div className="master-plans-page__empty">
					<p className="master-plans-page__empty-title">{i18n.t("master.coupons.empty")}</p>
					<p className="m-0 text-sm">{i18n.t("master.coupons.emptyHint")}</p>
					<button type="button" className="master-plans-page__create mt-2" onClick={openCreate}>
						<IconPlus />
						{i18n.t("master.coupons.create")}
					</button>
				</div>
			) : (
				<div className="master-plans-page__grid">
					{sortedCoupons.map((coupon, index) => (
						<CouponCard
							key={coupon.id}
							coupon={coupon}
							color={COUPON_COLORS[index % COUPON_COLORS.length]}
							deleting={deletingId === coupon.id}
							onEdit={openEdit}
							onDelete={handleOpenDeleteConfirm}
						/>
					))}
				</div>
			)}

			<MasterConfirmationModal
				title={
					couponToDelete
						? i18n.t("master.coupons.confirmDelete", { name: couponToDelete.name })
						: i18n.t("master.coupons.delete")
				}
				open={confirmDeleteOpen}
				onClose={setConfirmDeleteOpen}
				onConfirm={handleConfirmDelete}
			>
				{i18n.t("master.coupons.confirmDeleteMessage")}
			</MasterConfirmationModal>

			<MasterModal
				open={modalOpen}
				onClose={closeModal}
				title={
					editing
						? i18n.t("master.coupons.editTitle")
						: i18n.t("master.coupons.createTitle")
				}
				subtitle={i18n.t("master.coupons.formHint")}
				footer={modalFooter}
				maxWidth={480}
			>
				<form id="master-coupon-form" onSubmit={handleSubmit} className="space-y-1">
					<AuthField id="coupon-name" label={i18n.t("master.coupons.fieldName")}>
						<input
							id="coupon-name"
							type="text"
							className="form-input w-full"
							required
							minLength={2}
							value={form.name}
							onChange={(e) => setForm((f) => ({ ...f, name: e.target.value.toUpperCase() }))}
							autoFocus
						/>
					</AuthField>
					<AuthField id="coupon-type" label={i18n.t("master.coupons.fieldDiscountType")}>
						<select
							id="coupon-type"
							className="form-select w-full"
							value={form.discountType}
							onChange={(e) => setForm((f) => ({ ...f, discountType: e.target.value }))}
						>
							<option value="PERCENTAGE">{i18n.t("master.coupons.typePercentage")}</option>
							<option value="FIXED">{i18n.t("master.coupons.typeFixed")}</option>
						</select>
					</AuthField>
					<AuthField id="coupon-value" label={i18n.t("master.coupons.fieldDiscountValue")}>
						<input
							id="coupon-value"
							type="number"
							className="form-input w-full"
							required
							min={0}
							max={form.discountType === "PERCENTAGE" ? 100 : undefined}
							step="0.01"
							value={form.discountValue}
							onChange={(e) => setForm((f) => ({ ...f, discountValue: e.target.value }))}
						/>
					</AuthField>
					<AuthField>
						<label className="flex items-center gap-3 cursor-pointer mt-2">
							<input
								type="checkbox"
								className="w-5 h-5 rounded border-default-300 text-primary focus:ring-primary/30"
								checked={form.lifetime}
								onChange={(e) =>
									setForm((f) => ({
										...f,
										lifetime: e.target.checked,
										expiresAt: e.target.checked ? "" : f.expiresAt,
									}))
								}
							/>
							<span className="text-sm font-medium text-default-800 dark:text-default-200">
								{i18n.t("master.coupons.fieldLifetime")}
							</span>
						</label>
					</AuthField>
					{!form.lifetime && (
						<AuthField id="coupon-expires" label={i18n.t("master.coupons.fieldExpiresAt")}>
							<input
								id="coupon-expires"
								type="date"
								className="form-input w-full"
								required
								value={form.expiresAt}
								onChange={(e) =>
									setForm((f) => ({ ...f, expiresAt: e.target.value, lifetime: false }))
								}
							/>
						</AuthField>
					)}
					<AuthField
						id="coupon-email"
						label={i18n.t("master.coupons.fieldEmailRef")}
						hint={i18n.t("master.coupons.emailHint")}
					>
						<input
							id="coupon-email"
							type="email"
							className="form-input w-full"
							value={form.emailRef}
							onChange={(e) => setForm((f) => ({ ...f, emailRef: e.target.value }))}
						/>
					</AuthField>
					<AuthField>
						<label className="flex items-center gap-3 cursor-pointer mt-2">
							<input
								type="checkbox"
								className="w-5 h-5 rounded border-default-300 text-primary focus:ring-primary/30"
								checked={form.active}
								onChange={(e) =>
									setForm((f) => ({ ...f, active: e.target.checked }))
								}
							/>
							<span className="text-sm font-medium text-default-800 dark:text-default-200">
								{i18n.t("master.coupons.fieldActive")}
							</span>
						</label>
					</AuthField>
				</form>
			</MasterModal>
		</div>
	);
}
